import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { ControlAction } from "./DataControlParts";
import { useToast } from "../../../hooks/useToast";
import { UAE_PASS_STATUS_QUERY_KEY } from "../../../hooks/useUaePassStatus";
import { unlinkUaePass } from "../../../lib/auth-api";
import { confirm } from "../../../lib/confirm-action";
import { invalidateCvDocumentQueries } from "../../../lib/cv-query-invalidation";

export function UaePassDisconnectControl() {
  const { t } = useTranslation("common");
  const queryClient = useQueryClient();
  const toast = useToast();
  const lock = useRef(false);
  const [busy, setBusy] = useState(false);
  const disconnect = async () => {
    if (lock.current) return;
    lock.current = true;
    setBusy(true);
    try {
      if (
        !(await confirm({
          title: t("settings.dataManagement.disconnectTitle"),
          message: t("settings.dataManagement.disconnectBody"),
          confirmLabel: t("settings.dataManagement.disconnect"),
          cancelLabel: t("actions.cancel"),
          destructive: true,
        }))
      )
        return;
      await unlinkUaePass();
      // Drop cached provider fields immediately; the auth wrapper also clears
      // the verified badge in the current user store.
      await queryClient.cancelQueries({ queryKey: UAE_PASS_STATUS_QUERY_KEY });
      queryClient.setQueryData(
        UAE_PASS_STATUS_QUERY_KEY,
        (current: Record<string, unknown> | undefined) =>
          current
            ? {
                ...current,
                isLinked: false,
                isVerified: false,
                linkedAt: null,
                uaePassData: null,
              }
            : undefined,
      );
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["auth"] }),
        invalidateCvDocumentQueries(queryClient),
      ]);
      toast.success(t("settings.dataManagement.disconnected"));
    } catch {
      toast.error(t("settings.dataManagement.actionFailed"));
    } finally {
      lock.current = false;
      setBusy(false);
    }
  };
  return (
    <ControlAction
      title={t("settings.dataManagement.disconnect")}
      disabled={busy}
      onPress={() => void disconnect()}
    />
  );
}
